import { Card, CardActions, CardContent, Grid, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { PmiButton } from 'shell/components/shared/button';
import { useCallback, useState } from 'react';
import * as yup from 'yup';
import PmiTextField from 'shell/components/shared/text-field';
import { DateTimePicker } from '@mui/lab';
import { DateTime } from 'luxon';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { HttpResponse } from 'shell/models/http-response';
import { __OPERATION__EVENT__ADMIN__ } from 'shell/constants/environment';
import { HttpRequestFunction, ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import EventInErrorWarningModal from './warning-modal';
import { EventError } from 'shell/models/event-error';
import { transformTypeEventResponse } from 'system/pages/search-events-error/utils/transform-event-error';

export interface ErrorEventForm {
  idPublishedEvent?: number;
  idConsummedEvent?: number;
  dateTimePublication: DateTime | null;
  eventTypeCode?: string;
  destinationTypeCode?: string;
  subscriber?: string;
  eventData?: string;
  progressive?: number;
  eventDescription?: string;
  dateTimeConsumationStart: DateTime | null;
  dateTimeConsumationEnd: DateTime | null;
  attemptsNumber?: number;
  state?: string;
  outcome?: string;
  idJms?: string;
  destinationTypeDescription?: string;
  subscriberDescription?: string;
}

const validationSchema = yup.object().shape({
  outcome: yup.string(),
});

interface FormErrorEventProps {
  onResubmit: (form: ErrorEventForm) => ResponsePromise<void> | Promise<void> | void | undefined;
  onCancel: () => void;
  title: string;
  disabled?: boolean;
  initialValues?: ErrorEventForm;
  isEdit?: boolean;
  getEventError: HttpRequestFunction<HttpResponse<EventError>, transformTypeEventResponse>;
}

const FormErrorEvent = ({
  onResubmit,
  onCancel,
  title,
  disabled = true,
  initialValues,
  isEdit = false,
  getEventError,
}: FormErrorEventProps): React.ReactElement => {
  const [openWarningModal, setOpenWarningModal] = useState<{ warning?: string; open: boolean }>({
    open: false,
  });

  const checkErrorEvent = useHttpClient<HttpResponse<{ warning: Array<string> }>>(
    `${__OPERATION__EVENT__ADMIN__}/eventoInErrore/controllaEvento/${initialValues?.idConsummedEvent}`
  );

  const setOk = useHttpClient<void>(
    `${__OPERATION__EVENT__ADMIN__}/eventoInErrore/set-ok/${initialValues?.idConsummedEvent}`
  );

  const onSubmit = useCallback(
    (values) => {
      (checkErrorEvent() as ResponsePromise<HttpResponse<{ warning: Array<string> }>>).then(
        (response) => {
          if (response?.data.payload.warning && response?.data.payload.warning.length > 0) {
            setOpenWarningModal({
              open: true,
              warning: response.data.payload.warning.join(' '),
            });
            return;
          }

          onResubmit(values);
        }
      );
    },
    [checkErrorEvent, onResubmit]
  );

  const formErrorEvent = useFormik<ErrorEventForm>({
    initialValues: initialValues || {
      attemptsNumber: undefined,
      dateTimeConsumationEnd: null,
      dateTimeConsumationStart: null,
      dateTimePublication: null,
      destinationTypeCode: '',
      eventData: '',
      eventTypeCode: '',
      eventDescription: '',
      idConsummedEvent: undefined,
      idPublishedEvent: undefined,
      outcome: '',
      progressive: undefined,
      state: '',
      subscriber: '',
      idJms: '',
      subscriberDescription: '',
      destinationTypeDescription: '',
    },
    validationSchema: validationSchema,
    onSubmit,
    enableReinitialize: true,
  });

  return (
    <Card raised className="pmi-my-3">
      <form onSubmit={formErrorEvent.handleSubmit}>
        <CardContent style={{ padding: '24px' }}>
          <Typography gutterBottom variant="h5" component="h2" color="secondary">
            {title}
          </Typography>
          <div style={{ padding: '24px 0 0 0' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} container spacing={0} columnSpacing={1}>
                <Grid item xs={12} md={2}>
                  <PmiTextField
                    aria-label="ID evento consumato"
                    label="ID evento consumato"
                    id="idConsummedEvent"
                    name="idConsummedEvent"
                    type="text"
                    variant="outlined"
                    fullWidth
                    value={formErrorEvent.values.idConsummedEvent || ''}
                    disabled={disabled}
                    onChange={formErrorEvent.handleChange}
                    error={
                      formErrorEvent.touched.idConsummedEvent &&
                      Boolean(formErrorEvent.errors.idConsummedEvent)
                    }
                    helperText={formErrorEvent.errors.idConsummedEvent}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <PmiTextField
                    aria-label="ID evento Pubblicato"
                    label="ID evento Pubblicato"
                    id="idPublishedEvent"
                    name="idPublishedEvent"
                    type="text"
                    variant="outlined"
                    fullWidth
                    value={formErrorEvent.values.idPublishedEvent || ''}
                    disabled={disabled}
                    onChange={formErrorEvent.handleChange}
                    error={
                      formErrorEvent.touched.idPublishedEvent &&
                      Boolean(formErrorEvent.errors.idPublishedEvent)
                    }
                    helperText={formErrorEvent.errors.idPublishedEvent}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DateTimePicker
                    aria-label="Data e ora pubblicazione"
                    label="Data e ora pubblicazione"
                    disabled={disabled}
                    value={formErrorEvent.values.dateTimePublication || null}
                    onChange={formErrorEvent.handleChange}
                    renderInput={(params) => (
                      <PmiTextField
                        {...params}
                        id="dateTimePublication"
                        name="dateTimePublication"
                        fullWidth
                        error={
                          formErrorEvent.touched.dateTimePublication &&
                          Boolean(formErrorEvent.errors.dateTimePublication)
                        }
                        helperText={formErrorEvent.errors.dateTimePublication}
                      />
                    )}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} container spacing={0} columnSpacing={1}>
                <Grid item xs={12} md={2}>
                  <PmiTextField
                    aria-label="Tipo Evento"
                    label="Tipo Evento"
                    id="eventTypeCode"
                    name="eventTypeCode"
                    type="text"
                    variant="outlined"
                    fullWidth
                    value={formErrorEvent.values.eventTypeCode || ''}
                    disabled={disabled || isEdit}
                    onChange={formErrorEvent.handleChange}
                    error={
                      formErrorEvent.touched.eventTypeCode &&
                      Boolean(formErrorEvent.errors.eventTypeCode)
                    }
                    helperText={formErrorEvent.errors.eventTypeCode}
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <PmiTextField
                    aria-label="Descrizione del Tipo Evento"
                    label="Descrizione Tipo Evento"
                    id="eventDescription"
                    name="eventDescription"
                    type="text"
                    variant="outlined"
                    fullWidth
                    value={formErrorEvent.values.eventDescription || ''}
                    disabled={disabled || isEdit}
                    onChange={formErrorEvent.handleChange}
                    error={
                      formErrorEvent.touched.eventDescription &&
                      Boolean(formErrorEvent.errors.eventDescription)
                    }
                    helperText={formErrorEvent.errors.eventDescription}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} container spacing={0} columnSpacing={1}>
                <Grid item xs={12} md={2}>
                  <PmiTextField
                    aria-label="Tipo Destinazione"
                    label="Tipo Destinazione"
                    id="destinationTypeCode"
                    name="destinationTypeCode"
                    type="text"
                    variant="outlined"
                    fullWidth
                    value={formErrorEvent.values.destinationTypeCode || ''}
                    disabled={disabled || isEdit}
                    onChange={formErrorEvent.handleChange}
                    error={
                      formErrorEvent.touched.eventTypeCode &&
                      Boolean(formErrorEvent.errors.eventTypeCode)
                    }
                    helperText={formErrorEvent.errors.eventTypeCode}
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <PmiTextField
                    aria-label="Descrizione Tipo Destinazione"
                    label="Descrizione Tipo Destinazione"
                    id="destinationTypeDescription"
                    name="destinationTypeDescription"
                    type="text"
                    variant="outlined"
                    fullWidth
                    value={formErrorEvent.values.destinationTypeDescription || ''}
                    disabled={disabled || isEdit}
                    onChange={formErrorEvent.handleChange}
                    error={
                      formErrorEvent.touched.destinationTypeDescription &&
                      Boolean(formErrorEvent.errors.destinationTypeDescription)
                    }
                    helperText={formErrorEvent.errors.destinationTypeDescription}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} container spacing={0} columnSpacing={1}>
                <Grid item xs={12} md={2}>
                  <PmiTextField
                    aria-label="Sottoscrittore"
                    label="Sottoscrittore"
                    id="subscriber"
                    name="subscriber"
                    type="text"
                    variant="outlined"
                    fullWidth
                    value={formErrorEvent.values.subscriber || ''}
                    disabled={disabled || isEdit}
                    onChange={formErrorEvent.handleChange}
                    error={
                      formErrorEvent.touched.subscriber && Boolean(formErrorEvent.errors.subscriber)
                    }
                    helperText={formErrorEvent.errors.subscriber}
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <PmiTextField
                    aria-label="Descrizione Sottoscrittore"
                    label="Descrizione Sottoscrittore"
                    id="subscriberDescription"
                    name="subscriberDescription"
                    type="text"
                    variant="outlined"
                    fullWidth
                    value={formErrorEvent.values.subscriberDescription || ''}
                    disabled={disabled || isEdit}
                    onChange={formErrorEvent.handleChange}
                    error={
                      formErrorEvent.touched.subscriberDescription &&
                      Boolean(formErrorEvent.errors.subscriberDescription)
                    }
                    helperText={formErrorEvent.errors.subscriberDescription}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} container spacing={0}>
                <Grid item xs={12} md={10}>
                  <PmiTextField
                    aria-label="Dati dell'evento"
                    label="Dati dell'evento"
                    id="eventData"
                    name="eventData"
                    type="text"
                    multiline
                    rows={4}
                    variant="outlined"
                    fullWidth
                    value={formErrorEvent.values.eventData || ''}
                    disabled={disabled || isEdit}
                    onChange={formErrorEvent.handleChange}
                    error={
                      formErrorEvent.touched.eventData && Boolean(formErrorEvent.errors.eventData)
                    }
                    helperText={formErrorEvent.errors.eventData}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} container spacing={0} columnSpacing={1}>
                <Grid item xs={12} md={5}>
                  <DateTimePicker
                    aria-label="Data e ora inizio consumazione"
                    disabled={disabled}
                    label="Data e ora inizio consumazione"
                    value={formErrorEvent.values.dateTimeConsumationStart || null}
                    onChange={formErrorEvent.handleChange}
                    renderInput={(params) => (
                      <PmiTextField
                        {...params}
                        name="dateTimeConsumationStart"
                        id="dateTimeConsumationStart"
                        fullWidth
                        error={
                          formErrorEvent.touched.dateTimeConsumationStart &&
                          Boolean(formErrorEvent.errors.dateTimeConsumationStart)
                        }
                        helperText={formErrorEvent.errors.dateTimeConsumationStart}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={5}>
                  <DateTimePicker
                    aria-label="Data e ora fine consumazione"
                    disabled={disabled}
                    label="Data e ora fine consumazione"
                    value={formErrorEvent.values.dateTimeConsumationEnd || null}
                    onChange={formErrorEvent.handleChange}
                    renderInput={(params) => (
                      <PmiTextField
                        {...params}
                        fullWidth
                        id="dateTimeConsumationEnd"
                        name="dateTimeConsumationEnd"
                        error={
                          formErrorEvent.touched.dateTimeConsumationEnd &&
                          Boolean(formErrorEvent.errors.dateTimeConsumationEnd)
                        }
                        helperText={formErrorEvent.errors.dateTimeConsumationEnd}
                      />
                    )}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} container spacing={0} columnSpacing={1}>
                <Grid item xs={12} md={2}>
                  <PmiTextField
                    aria-label="Progressivo"
                    label="Progressivo"
                    id="progressive"
                    name="progressive"
                    type="text"
                    variant="outlined"
                    fullWidth
                    value={formErrorEvent.values.progressive || ''}
                    disabled={disabled || isEdit}
                    onChange={formErrorEvent.handleChange}
                    error={
                      formErrorEvent.touched.progressive &&
                      Boolean(formErrorEvent.errors.progressive)
                    }
                    helperText={formErrorEvent.errors.progressive}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <PmiTextField
                    aria-label="Num.tentativi"
                    label="Num.tentativi"
                    id="attemptsNumber"
                    name="attemptsNumber"
                    type="text"
                    variant="outlined"
                    fullWidth
                    value={formErrorEvent.values.attemptsNumber || ''}
                    disabled={disabled || isEdit}
                    onChange={formErrorEvent.handleChange}
                    error={
                      formErrorEvent.touched.attemptsNumber &&
                      Boolean(formErrorEvent.errors.attemptsNumber)
                    }
                    helperText={formErrorEvent.errors.attemptsNumber}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <PmiTextField
                    aria-label="Stato"
                    label="Stato"
                    id="state"
                    name="state"
                    type="text"
                    variant="outlined"
                    fullWidth
                    value={formErrorEvent.values.state || ''}
                    disabled={disabled || isEdit}
                    onChange={formErrorEvent.handleChange}
                    error={formErrorEvent.touched.state && Boolean(formErrorEvent.errors.state)}
                    helperText={formErrorEvent.errors.state}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} md={10}>
                <PmiTextField
                  aria-label="Esito"
                  label="Esito"
                  id="outcome"
                  name="outcome"
                  multiline
                  rows={4}
                  type="text"
                  variant="outlined"
                  fullWidth
                  value={formErrorEvent.values.outcome || ''}
                  disabled={disabled || isEdit}
                  onChange={formErrorEvent.handleChange}
                  error={formErrorEvent.touched.outcome && Boolean(formErrorEvent.errors.outcome)}
                  helperText={formErrorEvent.errors.outcome}
                />
              </Grid>
            </Grid>
          </div>
        </CardContent>
        <CardActions style={{ padding: '24px 24px 16px 24px' }}>
          <PmiButton variant="contained" color="primary" type="submit">
            Reinoltra
          </PmiButton>
          <PmiButton
            variant="outlined"
            color="cdmBrand"
            onClick={() => {
              onCancel();
            }}
          >
            Indietro
          </PmiButton>
        </CardActions>
      </form>
      {openWarningModal.open ? (
        <EventInErrorWarningModal
          open={openWarningModal.open}
          message={openWarningModal.warning || ''}
          onClose={() => setOpenWarningModal({ open: false })}
          onOk={() => {
            (setOk() as ResponsePromise<void>).then(() => {
              setOpenWarningModal({ open: false });
              getEventError();
            });
          }}
          onResubmit={() => {
            (onResubmit(formErrorEvent.values) as ResponsePromise<void>).then(() => {
              setOpenWarningModal({ open: false });
            });
          }}
        />
      ) : null}
    </Card>
  );
};

FormErrorEvent.displayName = 'Form Error Event component';

export default FormErrorEvent;
